import React, { useState, useEffect, useRef } from 'react';
import { Download, X, Share, PlusSquare, MoreVertical } from 'lucide-react';
import { db, trackFeatureUsage } from '../../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Resets on page reload, persists when switching apps
let dismissedThisSession = false;

const PwaInstallPrompt = ({ language = 'en', darkMode = false, trigger = false, autoDelay = 0 }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showManual, setShowManual] = useState(false); // Android/Desktop manual steps fallback
  const checkedRef = useRef(false);

  // Capture Android beforeinstallprompt event early
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Detect platform once
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    const ua = window.navigator.userAgent.toLowerCase();
    // iPadOS 13+ reports itself as "Macintosh" — detect via touch points
    const iOSDevice = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(iOSDevice);
    setIsAndroid(/android/.test(ua));
  }, []);

  // Auto-show after delay (for Login/Landing pages)
  useEffect(() => {
    if (!autoDelay || dismissedThisSession || isStandalone) return;

    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, autoDelay);
    return () => clearTimeout(timer);
  }, [autoDelay, isStandalone]);

  // Show prompt when triggered (after WelcomePopup dismissed, for Dashboard)
  useEffect(() => {
    if (!trigger || dismissedThisSession || isStandalone) return;

    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [trigger, isStandalone]);

  // Record install to Firestore (per-user + global counter)
  const recordPwaInstall = () => {
    try {
      const uid = getAuth().currentUser?.uid;
      if (uid) {
        const userRef = doc(db, 'users', uid);
        setDoc(userRef, {
          pwaInstalled: true,
          pwaInstalledAt: new Date().toISOString(),
          pwaDevice: /iphone|ipad|ipod/i.test(navigator.userAgent) ? 'ios' : 'android'
        }, { merge: true });
      }
      trackFeatureUsage('pwa_installed');
    } catch (e) { /* silent */ }
  };

  // Listen for appinstalled event (fires when user installs via any method)
  useEffect(() => {
    const handler = () => recordPwaInstall();
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Native install prompt isn't available (browser doesn't support it,
      // already used, or an in-app browser). Reveal manual step-by-step
      // instructions instead of silently doing nothing.
      setShowManual(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
      recordPwaInstall();
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    dismissedThisSession = true;
  };

  if (!showPrompt || isStandalone || (!isIOS && !isAndroid)) return null;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-[100] p-4 m-4 rounded-2xl shadow-2xl border animate-in slide-in-from-bottom-4 flex flex-col gap-3 ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-800'}`}>
      <button onClick={handleDismiss} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-full">
        <X size={14} />
      </button>

      <div className="flex items-center gap-4">
        <img src="/logo192.png" alt="PortPro" className="w-12 h-12 rounded-xl shrink-0 shadow-lg shadow-indigo-600/20" />
        
        <div className="flex-1 pr-4">
          <h4 className="font-bold text-[15px]">
            {language === 'en' ? 'Install PortPro' : 'ติดตั้งแอป PortPro'}
          </h4>
          <p className="text-[12px] opacity-80 mt-0.5 leading-tight">
            {language === 'en' 
              ? 'Install our app on your device for a faster, full-screen experience and offline access.' 
              : 'ติดตั้งแอปลงมือถือเพื่อใช้งานเต็มจอ โหลดไวขึ้น และไม่ต้องเข้าผ่านเบราว์เซอร์'}
          </p>
        </div>
      </div>

      {isIOS ? (
        <div className="flex flex-col gap-2 mt-1">
          <div className={`p-3 rounded-xl text-[12px] flex flex-col gap-2 ${darkMode ? 'bg-indigo-900/30 text-indigo-200' : 'bg-indigo-50 text-indigo-800'}`}>
            <div className="font-bold flex items-center gap-1.5">
              {language === 'en' ? 'How to install on iPhone / iPad:' : 'วิธีติดตั้งบน iPhone / iPad (Safari):'}
            </div>
            <ol className="list-decimal pl-4 space-y-1.5">
              <li className="flex items-center gap-2 flex-wrap">
                {language === 'en' ? 'Tap the Share button in Safari' : 'แตะปุ่มแชร์ (Share) ในแถบเมนู Safari'} <Share size={13} className="inline opacity-80" />
              </li>
              <li className="flex items-center gap-2 flex-wrap">
                {language === 'en' ? 'Scroll down and select "Add to Home Screen"' : 'เลื่อนลงแล้วเลือก "เพิ่มไปยังหน้าจอโฮม"'} <PlusSquare size={13} className="inline opacity-80" />
              </li>
              <li>
                {language === 'en' ? 'Tap "Add" — done!' : 'กด "เพิ่ม" (Add) มุมขวาบน — เป็นอันเสร็จ'}
              </li>
            </ol>
          </div>
          <button 
            onClick={() => { recordPwaInstall(); handleDismiss(); }}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-xl text-sm transition-colors flex justify-center items-center gap-2"
          >
            <Download size={16} />
            {language === 'en' ? "Done! I've installed it" : 'ติดตั้งแล้ว ✓'}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 mt-1">
          <button 
            onClick={handleInstallClick}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl text-sm transition-colors flex justify-center items-center gap-2"
          >
            <Download size={16} />
            {language === 'en' ? 'Install App Now' : 'ติดตั้งแอปเลย'}
          </button>

          {!showManual ? (
            <button
              onClick={() => setShowManual(true)}
              className="text-[12px] opacity-70 hover:opacity-100 underline self-center"
            >
              {language === 'en' ? "Button didn't work? See manual steps" : 'กดแล้วไม่ขึ้น? ดูวิธีติดตั้งด้วยตนเอง'}
            </button>
          ) : (
            <div className={`p-3 rounded-xl text-[12px] flex flex-col gap-2 ${darkMode ? 'bg-indigo-900/30 text-indigo-200' : 'bg-indigo-50 text-indigo-800'}`}>
              {isAndroid ? (
                <>
                  <div className="font-bold">{language === 'en' ? 'How to install on Android (Chrome):' : 'วิธีติดตั้งบน Android (Chrome):'}</div>
                  <ol className="list-decimal pl-4 space-y-1.5">
                    <li className="flex items-center gap-2 flex-wrap">
                      {language === 'en' ? 'Open the Chrome menu (top-right)' : 'แตะเมนู (จุด 3 จุด) มุมขวาบนของ Chrome'} <MoreVertical size={13} className="inline opacity-80" />
                    </li>
                    <li>{language === 'en' ? 'Tap "Install app" or "Add to Home screen"' : 'เลือก "ติดตั้งแอป" หรือ "เพิ่มลงในหน้าจอหลัก"'}</li>
                    <li>{language === 'en' ? 'Confirm by tapping "Install"' : 'กด "ติดตั้ง" เพื่อยืนยัน'}</li>
                  </ol>
                </>
              ) : (
                <>
                  <div className="font-bold">{language === 'en' ? 'How to install on desktop:' : 'วิธีติดตั้งบนคอมพิวเตอร์:'}</div>
                  <ol className="list-decimal pl-4 space-y-1.5">
                    <li className="flex items-center gap-2 flex-wrap">
                      {language === 'en' ? 'Click the install icon in the address bar' : 'คลิกไอคอนติดตั้งในแถบที่อยู่ (มุมขวาบน)'} <Download size={13} className="inline opacity-80" />
                    </li>
                    <li>{language === 'en' ? 'Or open the browser menu → "Install PortPro…"' : 'หรือเปิดเมนูเบราว์เซอร์ → "ติดตั้ง PortPro…"'}</li>
                    <li>{language === 'en' ? 'Confirm by clicking "Install"' : 'กด "ติดตั้ง" เพื่อยืนยัน'}</li>
                  </ol>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PwaInstallPrompt;
